import { supabase } from '../../lib/supabase';
import { LaunchTask, LaunchCalendar, TaskTemplate, TaskStatus, TaskCategory } from '../../types/launchpad';
import { v4 as uuidv4 } from 'uuid';
import { defaultTaskTemplates } from './launchpadTaskTemplates';

/**
 * Create a new launch calendar for a user
 */
export const createLaunchCalendar = async (userId: string, businessId?: string, startDate?: string): Promise<LaunchCalendar | null> => {
  try {
    const calendarStartDate = startDate || new Date().toISOString();
    const newCalendar: Omit<LaunchCalendar, 'id' | 'created_at' | 'updated_at'> = {
      user_id: userId,
      business_id: businessId,
      start_date: calendarStartDate,
      current_day: 1,
      last_updated: calendarStartDate,
      progress: 0,
      category_progress: {
        legal: 0,
        finance: 0,
        marketing: 0,
        operations: 0,
        product: 0,
        sales: 0,
        hr: 0,
        admin: 0,
        research: 0,
        planning: 0
      }
    };

    const { data, error } = await supabase
      .from('launch_calendars')
      .insert(newCalendar)
      .select()
      .single();

    if (error) {
      console.error('Error creating launch calendar:', error);
      return null;
    }

    // Initialize with default tasks
    await initializeDefaultTasks(userId, data.id, businessId);

    return data;
  } catch (error) {
    console.error('Error in createLaunchCalendar:', error);
    return null;
  }
};

/**
 * Get a user's launch calendar
 */
export const getLaunchCalendar = async (userId: string, businessId?: string): Promise<LaunchCalendar | null> => {
  try {
    let query = supabase
      .from('launch_calendars')
      .select('*')
      .eq('user_id', userId);
    
    if (businessId) {
      query = query.eq('business_id', businessId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('Error fetching launch calendar:', error);
      return null;
    }

    if (!data) {
      // Create a new calendar if none exists
      return await createLaunchCalendar(userId, businessId);
    }

    return data;
  } catch (error) {
    console.error('Error in getLaunchCalendar:', error);
    return null;
  }
};

/**
 * Update a launch calendar
 */
export const updateLaunchCalendar = async (calendar: Partial<LaunchCalendar> & { id: string }): Promise<LaunchCalendar | null> => {
  try {
    const { data, error } = await supabase
      .from('launch_calendars')
      .update({
        ...calendar,
        updated_at: new Date().toISOString()
      })
      .eq('id', calendar.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating launch calendar:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in updateLaunchCalendar:', error);
    return null;
  }
};

/**
 * Initialize default tasks for a new launch calendar
 */
const initializeDefaultTasks = async (userId: string, calendarId: string, businessId?: string): Promise<void> => {
  try {
    const tasks = defaultTaskTemplates.map((template: TaskTemplate) => ({
      id: uuidv4(),
      user_id: userId,
      business_id: businessId,
      calendar_id: calendarId,
      title: template.title,
      description: template.description,
      status: 'pending' as TaskStatus,
      priority: template.priority,
      category: template.category,
      due_date: calculateDueDate(template.scheduled_day),
      scheduled_day: template.scheduled_day,
      resources: template.resources || [],
      dependencies: template.dependencies || [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));

    const { error } = await supabase
      .from('launch_tasks')
      .insert(tasks);

    if (error) {
      console.error('Error initializing default tasks:', error);
    }
  } catch (error) {
    console.error('Error in initializeDefaultTasks:', error);
  }
};

/**
 * Calculate due date based on scheduled day
 */
const calculateDueDate = (scheduledDay: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + scheduledDay);
  return date.toISOString();
};

/**
 * Get tasks for a specific date range
 */
export const getTasksForDateRange = async (
  userId: string,
  startDay: number,
  endDay: number,
  businessId?: string
): Promise<LaunchTask[]> => {
  try {
    let query = supabase
      .from('launch_tasks')
      .select('*')
      .eq('user_id', userId)
      .gte('scheduled_day', startDay)
      .lte('scheduled_day', endDay)
      .order('scheduled_day', { ascending: true });
    
    if (businessId) {
      query = query.eq('business_id', businessId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching tasks for date range:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getTasksForDateRange:', error);
    return [];
  }
};

/**
 * Get all tasks for a user's launch calendar
 */
export const getAllLaunchTasks = async (userId: string, businessId?: string): Promise<LaunchTask[]> => {
  try {
    let query = supabase
      .from('launch_tasks')
      .select('*')
      .eq('user_id', userId)
      .order('scheduled_day', { ascending: true });
    
    if (businessId) {
      query = query.eq('business_id', businessId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching all launch tasks:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getAllLaunchTasks:', error);
    return [];
  }
};

/**
 * Update a task's status
 */
export const updateTaskStatus = async (taskId: string, status: TaskStatus): Promise<LaunchTask | null> => {
  try {
    const updates: Partial<LaunchTask> = {
      status,
      updated_at: new Date().toISOString()
    };

    // If completing the task, add completion timestamp
    if (status === 'completed') {
      updates.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('launch_tasks')
      .update(updates)
      .eq('id', taskId)
      .select()
      .single();

    if (error) {
      console.error('Error updating task status:', error);
      return null;
    }

    // Update calendar progress after task status change
    await updateCalendarProgress(data.user_id, data.business_id);

    return data;
  } catch (error) {
    console.error('Error in updateTaskStatus:', error);
    return null;
  }
};

/**
 * Update a task
 */
export const updateTask = async (task: Partial<LaunchTask> & { id: string }): Promise<LaunchTask | null> => {
  try {
    const { data, error } = await supabase
      .from('launch_tasks')
      .update({
        ...task,
        updated_at: new Date().toISOString()
      })
      .eq('id', task.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating task:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in updateTask:', error);
    return null;
  }
};

/**
 * Create a custom task
 */
export const createCustomTask = async (task: Omit<LaunchTask, 'id' | 'created_at' | 'updated_at'>): Promise<LaunchTask | null> => {
  try {
    const newTask = {
      ...task,
      id: uuidv4(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('launch_tasks')
      .insert(newTask)
      .select()
      .single();

    if (error) {
      console.error('Error creating custom task:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in createCustomTask:', error);
    return null;
  }
};

/**
 * Delete a task
 */
export const deleteTask = async (taskId: string): Promise<boolean> => {
  try {
    // Get the task first to know the user_id and business_id for progress update
    const { data: task } = await supabase
      .from('launch_tasks')
      .select('user_id, business_id')
      .eq('id', taskId)
      .single();

    const { error } = await supabase
      .from('launch_tasks')
      .delete()
      .eq('id', taskId);

    if (error) {
      console.error('Error deleting task:', error);
      return false;
    }

    // Update calendar progress after task deletion
    if (task) {
      await updateCalendarProgress(task.user_id, task.business_id);
    }

    return true;
  } catch (error) {
    console.error('Error in deleteTask:', error);
    return false;
  }
};

/**
 * Update calendar progress based on completed tasks
 */
export const updateCalendarProgress = async (userId: string, businessId?: string): Promise<void> => {
  try {
    // Get all tasks for this calendar
    const tasks = await getAllLaunchTasks(userId, businessId);
    
    if (tasks.length === 0) return;

    // Calculate overall progress
    const completedTasks = tasks.filter(task => task.status === 'completed').length;
    const totalTasks = tasks.length;
    const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    // Calculate category progress
    const categoryProgress: Record<TaskCategory, number> = {
      legal: 0,
      finance: 0,
      marketing: 0,
      operations: 0,
      product: 0,
      sales: 0,
      hr: 0,
      admin: 0,
      research: 0,
      planning: 0
    };

    // Group tasks by category
    const tasksByCategory: Record<TaskCategory, LaunchTask[]> = {
      legal: [],
      finance: [],
      marketing: [],
      operations: [],
      product: [],
      sales: [],
      hr: [],
      admin: [],
      research: [],
      planning: []
    };

    tasks.forEach(task => {
      tasksByCategory[task.category].push(task);
    });

    // Calculate progress for each category
    Object.entries(tasksByCategory).forEach(([category, categoryTasks]) => {
      if (categoryTasks.length === 0) return;
      
      const completedCategoryTasks = categoryTasks.filter(task => task.status === 'completed').length;
      categoryProgress[category as TaskCategory] = Math.round((completedCategoryTasks / categoryTasks.length) * 100);
    });

    // Get the calendar
    const { data: calendar } = await supabase
      .from('launch_calendars')
      .select('*')
      .eq('user_id', userId)
      .eq('business_id', businessId || '')
      .maybeSingle();

    if (!calendar) return;

    // Update the calendar progress
    await supabase
      .from('launch_calendars')
      .update({
        progress,
        category_progress: categoryProgress,
        last_updated: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', calendar.id);

  } catch (error) {
    console.error('Error in updateCalendarProgress:', error);
  }
};

/**
 * Get tasks due today or overdue
 */
export const getTasksDueToday = async (userId: string, businessId?: string): Promise<LaunchTask[]> => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayIso = today.toISOString();

    let query = supabase
      .from('launch_tasks')
      .select('*')
      .eq('user_id', userId)
      .lte('due_date', todayIso)
      .not('status', 'eq', 'completed')
      .not('status', 'eq', 'skipped')
      .order('due_date', { ascending: true });
    
    if (businessId) {
      query = query.eq('business_id', businessId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching tasks due today:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getTasksDueToday:', error);
    return [];
  }
};

/**
 * Advance the calendar to the next day
 */
export const advanceCalendarDay = async (calendarId: string): Promise<LaunchCalendar | null> => {
  try {
    // Get current calendar
    const { data: calendar, error: calendarError } = await supabase
      .from('launch_calendars')
      .select('*')
      .eq('id', calendarId)
      .single();

    if (calendarError || !calendar) {
      console.error('Error fetching calendar for advancement:', calendarError);
      return null;
    }

    // Increment the day
    const { data, error } = await supabase
      .from('launch_calendars')
      .update({
        current_day: calendar.current_day + 1,
        last_updated: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', calendarId)
      .select()
      .single();

    if (error) {
      console.error('Error advancing calendar day:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in advanceCalendarDay:', error);
    return null;
  }
};
